
import { computed, defineComponent, onMounted, PropType, reactive } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'

// validations
import {
    AdditionalServicesFTLI,
    AdditionalServiceShortModalFTLI,
} from '@/core/types/additional_services.types'

import { DowntimeFullT } from '@/core/types/orders.types'

import useVuelidate from '@vuelidate/core'
import { requiredIf } from '@vuelidate/validators'

export default defineComponent({
    components: {
        Modal,
        TheInput,
    },
    emits: ['cancel', 'submitted'],
    props: {
        isLoading: {
            type: Boolean,
            default: false,
        },
        additionalServices: {
            type: Array as PropType<AdditionalServicesFTLI[]>,
            default: () => [],
        },
        stocks: {
            type: Array as PropType<{ additional_service: AdditionalServicesFTLI[] }[]>,
            default: () => [],
        },
        ftlType: {
            type: Number,
            default: null,
        },
        paymentRateTypeId: {
            type: Number,
            default: null,
        },
        downtime: {
            type: Object as PropType<DowntimeFullT>,
            default: null,
        },
        statusCode: {
            type: String,
            default: null,
        },
        senderAddress: {
            type: String,
            default: null,
        },
        isNeedCar: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, { emit }) {
        const state = reactive({
            is_loading: false,
        })

        const delivery_state = reactive({
            additional_services: [] as AdditionalServiceShortModalFTLI[],
            stock_services: [] as AdditionalServiceShortModalFTLI[],
            stocks: [] as { additional_service: AdditionalServicesFTLI[] }[],
            has_downtime: false,
            downtime: {
                type: 1,
                hours_for_client: null as string | number | null,
                paid_price: null as string | number | null,
            },
            paid_price_total: null as number | null,
        })

        const isDelivered = computed(() => props.statusCode === 'delivered')
        const rateType = computed(() => {
            if (props.paymentRateTypeId === 1) return 'за рейс'
            if (props.paymentRateTypeId === 2) return 'за час'
            return ''
        })

        const state_form$ = useVuelidate(
            {
                downtime: {
                    hours_for_client: {
                        required: requiredIf(
                            () => delivery_state.has_downtime && isDelivered.value
                        ),
                    },
                    paid_price: {
                        required: requiredIf(
                            () => delivery_state.has_downtime && isDelivered.value
                        ),
                    },
                },
                paid_price_total: {
                    required: requiredIf(() => props.isNeedCar),
                },
            },
            delivery_state
        )

        const emitCancel = () => {
            emit('cancel')
        }

        const getAllAdditionalServices = () => {
            const additional_services = delivery_state.additional_services.map((service) => {
                return {
                    id: service.id,
                    paid_price_per_hour: Number(service.paid_price_per_hour),
                }
            })

            delivery_state.stocks.forEach((stock) => {
                stock.additional_service.forEach((service) => {
                    additional_services.push({
                        id: service.id,
                        paid_price_per_hour: Number(service.paid_price_per_hour)
                    })
                })
            })

            return additional_services
        }

        const onChangeStockAdditionalService = (stock_index: number, service_index: number, value: string) => {
            if (Boolean(value)) {
                delivery_state.stocks[stock_index].additional_service[service_index].paid_price_per_hour = Number(value)
            } else {
                delivery_state.stocks[stock_index].additional_service[service_index].paid_price_per_hour = 0
            }
        }

        const emitSubmitted = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (delivery_state.has_downtime) {
                emit('submitted', {
                    additional_services: getAllAdditionalServices(),
                    has_downtime: delivery_state.has_downtime,
                    downtime: {
                        hours_for_client: Number(delivery_state.downtime.hours_for_client),
                        paid_price: Number(delivery_state.downtime.paid_price),
                    },
                    paid_price_total: Number(delivery_state.paid_price_total),
                })
            } else {
                emit('submitted', {
                    additional_services: getAllAdditionalServices(),
                    paid_price_total: Number(delivery_state.paid_price_total),
                })
            }
        }

        const fillAdditionalServices = () => {
            delivery_state.additional_services = []
            delivery_state.stock_services = []

            props.additionalServices?.forEach((service) => {
                let new_service: AdditionalServiceShortModalFTLI = {
                    id: service.id,
                    type_name: service.type_name,
                    type_code: service.type_code,
                    paid_price_per_hour: service.paid_price_per_hour,
                }

                delivery_state.additional_services.push(new_service)
            })

            props.stocks?.forEach((stock) => {
                stock.additional_service.forEach((service) => {
                    let new_service: AdditionalServiceShortModalFTLI = {
                        id: service.id,
                        type_name: service.type_name,
                        type_code: service.type_code,
                        paid_price_per_hour: service.paid_price_per_hour,
                    }

                    delivery_state.stock_services.push(new_service)
                })
            })
        }

        onMounted(() => {
            if (isDelivered.value && props.downtime) {
                delivery_state.has_downtime = true
                delivery_state.downtime.type = props.downtime.type
            }
            
            delivery_state.stocks = props.stocks
            fillAdditionalServices()
        })

        return {
            state,
            state_form$,
            delivery_state,
            emitCancel,
            emitSubmitted,
            isDelivered,
            rateType,
            onChangeStockAdditionalService,
        }
    },
})
